import React from 'react';
import { useLanguage } from '../../../../hooks/useLanguage';
import { appData as portfolioData, getPortfolioText } from '../../../../data/portfolioData';
import { appData } from '../../../../data/appData';
import './Services.css';

const Services = () => {
  const { getText, currentLanguage } = useLanguage();
  
  // Obtener los datos de about del appData usando el idioma actual
  const aboutData = appData.languages[currentLanguage]?.about;

  // Función para abrir WhatsApp
  const openWhatsApp = () => {
    const message = currentLanguage === 'es' 
      ? "¡Hola! Me interesa saber más sobre tus servicios de desarrollo web." 
      : "Hello! I'm interested in learning more about your web development services.";
    const whatsappUrl = `https://wa.me/+50683714550?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getServiceIcon = (service) => {
    const iconMap = {
      'Desarrollo Web Frontend': '🎨',
      'Frontend Web Development': '🎨',
      'Desarrollo Web Backend': '⚙️',
      'Backend Web Development': '⚙️',
      'Aplicaciones Full-Stack': '🚀',
      'Full-Stack Applications': '🚀',
      'E-commerce': '🛒',
      'Sitios Web Corporativos': '🏢',
      'Corporate Websites': '🏢',
      'Landing Pages': '📱',
      'Optimización SEO': '📈',
      'SEO Optimization': '📈',
      'Mantenimiento Web': '🔧',
      'Web Maintenance': '🔧',
      'Consultoría Técnica': '💡',
      'Technical Consulting': '💡',
      'Migración de Sitios': '🔄',
      'Site Migration': '🔄'
    };
    const serviceName = getPortfolioText(service.name, currentLanguage);
    return iconMap[serviceName] || '⭐';
  };

  const getPriceColor = (price) => {
    if (!price || typeof price !== 'string') {
      return { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
    }
    
    if (price.includes('Consultar') || price.includes('Contact')) {
      return { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
    } else if (price.includes('$500')) {
      return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
    } else if (price.includes('$1000')) {
      return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
    } else {
      return { bg: '#fef3c7', color: '#92400e', border: '#fed7aa' };
    }
  };

  return (
    <section className="services-section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">{getText('portfolio.services.title')}</span>
          <h2 className="section-title">{getText('portfolio.services.subtitle')}</h2>
          <p className="section-description">
            {getText('portfolio.services.subtitle')}
          </p>
        </div>

        <div className="services-grid">
          {portfolioData.services && portfolioData.services.length > 0 ? portfolioData.services.map((service, index) => {
            const priceStyle = getPriceColor(getPortfolioText(service.price, currentLanguage));
            return (
              <div key={index} className="service-card">
                <div className="service-header">
                  <div className="service-icon">
                    {getServiceIcon(service)}
                  </div>
                  <div className="service-info">
                    <h3 className="service-name">
                      {getPortfolioText(service.name, currentLanguage)}
                    </h3>
                    <div 
                      className="service-price"
                      style={{
                        background: priceStyle.bg,
                        color: priceStyle.color,
                        border: `1px solid ${priceStyle.border}`
                      }}
                    >
                      {getPortfolioText(service.price, currentLanguage)}
                    </div>
                  </div>
                </div>

                <p className="service-description">
                  {getPortfolioText(service.description, currentLanguage)}
                </p>

                <div className="service-features">
                  <h4>{getText('portfolio.services.features')}:</h4>
                  <ul>
                    {service.features?.map((feature, idx) => (
                      <li key={idx}>{getPortfolioText(feature, currentLanguage)}</li>
                    ))}
                  </ul>
                </div>

                <div className="service-technologies">
                  <h4>{getText('portfolio.services.technologies')}:</h4>
                  <div className="tech-tags">
                    {service.technologies.map((tech, idx) => (
                      <span key={idx} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>

                <div className="service-footer">
                  <div className="delivery-time">
                    <span className="time-icon">⏱️</span>
                    <span>{getPortfolioText(service.deliveryTime, currentLanguage)}</span>
                  </div>
                  <button onClick={openWhatsApp} className="contact-btn">
                    {getText('portfolio.services.contactService')}
                    <span className="btn-arrow">→</span>
                  </button>
                </div>
              </div>
            );
          }) : (
            <div>{getText('common.noResults')}</div>
          )}
        </div>

        <div className="services-cta">
          <div className="cta-content">
            <h3>{getText('portfolio.contact.description')}</h3>
            <p>
              {getText('portfolio.contact.subtitle')}
            </p>
            <div className="cta-actions">
              <button onClick={openWhatsApp} className="contact-btn">
                💬 {getText('portfolio.services.contactService')}
              </button>
            </div>
          </div>
          
          <div className="cta-features">
            <div className="feature-item">
              <span className="feature-icon">✨</span>
              <div>
                <h4>{aboutData?.features?.items?.[0]?.title || getText('about.features.items.0.title')}</h4>
                <p>{aboutData?.features?.items?.[0]?.description || getText('about.features.items.0.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">🚀</span>
              <div>
                <h4>{getText('portfolio.services.deliveryTime')}</h4>
                <p>{aboutData?.features?.items?.[1]?.description || getText('about.features.items.1.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">🛡️</span>
              <div>
                <h4>{aboutData?.features?.items?.[2]?.title || getText('about.features.items.2.title')}</h4>
                <p>{aboutData?.features?.items?.[2]?.description || getText('about.features.items.2.description')}</p>
              </div>
            </div>
            
            <div className="feature-item">
              <span className="feature-icon">📱</span>
              <div>
                <h4>100% Responsive</h4>
                <p>{aboutData?.features?.items?.[3]?.description || getText('about.features.items.3.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
